/**
 * @file Contém funções utilitárias para cálculos geoespaciais.
 */

/**
 * Calcula uma "bounding box" (caixa delimitadora) a partir de um ponto central e um raio.
 * @param {{lat: number, lng: number}} center - O ponto central com latitude e longitude.
 * @param {number} radiusInMeters - O raio em metros.
 * @returns {string} Uma string no formato "minLng,minLat,maxLng,maxLat" para ser usada na API.
 */
export const calculateBbox = (center, radiusInMeters) => {
    const lat = center.lat;
    const lon = center.lng;
    const metersPerDegree = 111320; 
    const latOffset = radiusInMeters / metersPerDegree;
    const lonOffset = radiusInMeters / (metersPerDegree * Math.cos(lat * (Math.PI / 180)));
    const minLat = lat - latOffset;
    const maxLat = lat + latOffset;
    const minLng = lon - lonOffset;
    const maxLng = lon + lonOffset;
    return `${minLng},${minLat},${maxLng},${maxLat}`;
};

/**
 * Calcula a distância entre dois pontos geográficos usando a fórmula de Haversine.
 * @param {{lat: number, lng: number}} point1 - O primeiro ponto.
 * @param {{lat: number, lng: number}} point2 - O segundo ponto.
 * @returns {number} A distância em metros.
 */
export const haversineDistance = (point1, point2) => {
    const R = 6371e3; // Raio da Terra em metros
    const φ1 = point1.lat * Math.PI / 180;
    const φ2 = point2.lat * Math.PI / 180;
    const Δφ = (point2.lat - point1.lat) * Math.PI / 180;
    const Δλ = (point2.lng - point1.lng) * Math.PI / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Converte uma cor hexadecimal do Ozmap (RRGGBBAA) para um formato RGBA do CSS.
 * @param {string} hexColor - A cor no formato hexadecimal (ex: "ff550080").
 * @returns {string} A cor em formato rgba() ou um fallback.
 */
export const parseOzmapColor = (hexColor) => {
    if (!hexColor) return 'rgba(100, 100, 100, 0.5)'; // Cor padrão

    // Remove o caractere '#' se estiver presente
    if (hexColor.startsWith('#')) {
        hexColor = hexColor.slice(1);
    }

    if (hexColor.length < 8) {
        return 'rgba(100, 100, 100, 0.5)'; // Cor padrão se inválida
    }

    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);
    const a = parseInt(hexColor.substring(6, 8), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
};

/**
 * Verifica se um ponto está dentro de um polígono.
 * @param {{lat: number, lng: number}} point - O ponto a ser verificado.
 * @param {Array<Array<number>>} polygon - O polígono como array de coordenadas [lat, lng].
 * @returns {boolean} True se o ponto estiver dentro do polígono.
 */
export const isPointInPolygon = (point, polygon) => {
    const lat = point.lat;
    const lng = point.lng;

    let isInside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][1], yi = polygon[i][0];
        const xj = polygon[j][1], yj = polygon[j][0];

        const intersect = ((yi > lat) !== (yj > lat))
            && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
        if (intersect) isInside = !isInside;
    }
    return isInside;
};
